/**
 * PersonaScorecardCard — renders a single persona's review output.
 *
 * SRP: pure presentation. Takes one PersonaScorecard + optional
 * onIssueClick callback. Renders:
 *   - persona name + version chip
 *   - overall verdict badge, colour-tiered per severity
 *   - cost + duration footnote
 *   - issues list; each issue shows severity chip, description,
 *     rubric anchor, ICH E3 reference, draft excerpt (quoted),
 *     suggestion
 *   - clicking an issue fires ``onIssueClick(issue)`` — host
 *     (SectionDetailPanel / ChatFeedRenderer) decides what happens
 *     (scroll to excerpt in Word, open a details dialog, etc.)
 *
 * IA-agnostic: same component mounts in both IAs. Host chooses
 * the click handler.
 *
 * Spec: docs/specs/10-review-agent-sidecar.md § PersonaScorecard.
 */

import { Badge, Caption1, Text, makeStyles, tokens } from "@fluentui/react-components";
import type React from "react";
import type { PersonaIssue, PersonaScorecard, Severity, Verdict } from "./types";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  personaName: {
    fontWeight: tokens.fontWeightSemibold,
  },
  footnote: {
    color: tokens.colorNeutralForeground3,
  },
  issueList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  issue: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    borderLeft: `3px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusSmall,
    cursor: "default",
  },
  issueClickable: {
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  issueHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  excerpt: {
    fontStyle: "italic",
    color: tokens.colorNeutralForeground2,
    borderLeft: `2px solid ${tokens.colorNeutralStroke2}`,
    paddingLeft: tokens.spacingHorizontalS,
  },
  noIssues: {
    padding: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },
});

// ---------------------------------------------------------------------------
// Colour tiers
// ---------------------------------------------------------------------------

const verdictColor: Record<Verdict, "success" | "warning" | "severe" | "danger"> = {
  pass: "success",
  minor_revisions: "warning",
  major_revisions: "severe",
  refuse_to_file: "danger",
};

const verdictLabel: Record<Verdict, string> = {
  pass: "Pass",
  minor_revisions: "Minor revisions",
  major_revisions: "Major revisions",
  refuse_to_file: "Refuse to file",
};

const severityColor: Record<Severity, "warning" | "severe" | "danger"> = {
  minor: "warning",
  major: "severe",
  critical: "danger",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface PersonaScorecardCardProps {
  scorecard: PersonaScorecard;
  /** Called when an issue is clicked. Hosts typically scroll to
   * the draft excerpt in Word or open a details dialog. */
  onIssueClick?: (issue: PersonaIssue) => void;
}

export const PersonaScorecardCard: React.FC<PersonaScorecardCardProps> = ({ scorecard, onIssueClick }) => {
  const styles = useStyles();
  const clickable = typeof onIssueClick === "function";

  return (
    <section className={styles.root} aria-label={`Review by ${scorecard.persona_id}`}>
      <header className={styles.header}>
        <Text className={styles.personaName}>{scorecard.persona_id}</Text>
        <Caption1>v{scorecard.persona_version}</Caption1>
        <Badge appearance="filled" color={verdictColor[scorecard.overall_verdict]}>
          {verdictLabel[scorecard.overall_verdict]}
        </Badge>
      </header>

      <Caption1 className={styles.footnote}>
        {scorecard.module_id} · {scorecard.scope} · ${scorecard.cost_usd.toFixed(3)} · {scorecard.duration_ms}ms ·{" "}
        {scorecard.model_version_snapshot}
      </Caption1>

      {scorecard.issues.length === 0 ? (
        <div className={styles.noIssues}>No issues flagged.</div>
      ) : (
        <div className={styles.issueList}>
          {scorecard.issues.map((issue, i) => (
            <IssueRow key={`${issue.rubric_ref}-${i}`} issue={issue} clickable={clickable} onClick={onIssueClick} />
          ))}
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Issue row — pulled out so it doesn't bloat the main component
// ---------------------------------------------------------------------------

interface IssueRowProps {
  issue: PersonaIssue;
  clickable: boolean;
  onClick?: (issue: PersonaIssue) => void;
}

const IssueRow: React.FC<IssueRowProps> = ({ issue, clickable, onClick }) => {
  const styles = useStyles();

  const inner = (
    <>
      <div className={styles.issueHeader}>
        <Badge appearance="tint" color={severityColor[issue.severity]}>
          {issue.severity}
        </Badge>
        <Text weight="semibold">{issue.description}</Text>
      </div>
      <Caption1>
        {issue.rubric_ref}
        {issue.ich_e3_section && <> · ICH E3 §{issue.ich_e3_section}</>}
      </Caption1>
      {issue.draft_excerpt && <Text className={styles.excerpt}>&ldquo;{issue.draft_excerpt}&rdquo;</Text>}
      {issue.suggestion && (
        <Caption1>
          <strong>Suggestion:</strong> {issue.suggestion}
        </Caption1>
      )}
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        className={`${styles.issue} ${styles.issueClickable}`}
        onClick={() => onClick?.(issue)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick?.(issue);
          }
        }}
        aria-label={`Issue: ${issue.description}`}
      >
        {inner}
      </button>
    );
  }
  return <div className={styles.issue}>{inner}</div>;
};
